import {
  type ReactNode,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash-es';
import {
  SciChartSurface,
  SciChartJSLightTheme,
  NumericAxis,
  LogarithmicAxis,
  XyScatterRenderableSeries,
  XyDataSeries,
  EllipsePointMarker,
  VerticalLineAnnotation,
  HorizontalLineAnnotation,
  CustomAnnotation,
  MouseWheelZoomModifier,
  ZoomPanModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  CursorModifier,
  ChartModifierBase2D,
  ModifierMouseArgs,
  IPointMetadata,
  TSciChart,
  EAutoRange,
  EAnnotationLayer,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  EAxisAlignment,
  NumericLabelProvider,
  NumberRange,
  Thickness,
  TickProvider,
} from 'scichart';
import { formatNumber } from 'utils/number';
import type { ProbitData } from 'types/dca-workspaces';
import {
  SciChartExportImageButton,
  createSciChartElementHooks,
} from 'components/chart-export';
import { ensureWasmConfigured } from 'components/charts/scichart/ensure-wasm-configured';
import styles from './probit-chart.module.scss';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000)
    return `${+(value / 1_000_000_000).toPrecision(3)}B`;
  if (abs >= 1_000_000) return `${+(value / 1_000_000).toPrecision(3)}M`;
  if (abs >= 1_000) return `${+(value / 1_000).toPrecision(3)}k`;
  if (abs >= 1) return `${+value.toPrecision(3)}`;
  if (abs === 0) return '0';
  return value.toPrecision(2);
}

function formatDecadeLabel(v: number): string {
  if (v <= 0) return ' ';
  const log = Math.log10(v);
  if (Math.abs(log - Math.round(log)) > 0.01) return ' ';
  const pow = Math.round(log);
  if (pow < 3) return formatNumber(v, 0, 0, 'decimal', false);
  if (pow < 6) return `${formatNumber(v / 1e3, 0, 0, 'decimal', false)}K`;
  return `${formatNumber(v / 1e6, 0, 0, 'decimal', false)}M`;
}

/** Vertical gap between a reference-line label and the value beneath it. */
const REFERENCE_LABEL_LINE_HEIGHT = 12;

/** Plot-area background, reused as the halo that lifts a caption off lines. */
const CHART_BACKGROUND = '#FFFFFF';

/**
 * Inline SVG for a reference-line caption: each entry of `lines` on its own
 * row, 12px apart, with blank entries dropped. Used for the percentile and
 * Type Curve captions, where the first row is the label and the second (when
 * present) is the value the line sits at.
 *
 * The label stays the svg's first child because SciChart anchors the
 * annotation off `svg.firstChild.getBBox()` (see `SvgAnnotationBase`), so
 * adding a value row leaves every existing caption exactly where it sits.
 * Callers pass their own offsets — those are what keep each caption clear of
 * its own line.
 *
 * `pos.halo` paints a background-coloured stroke under each row's glyphs
 * (`paint-order="stroke"`), so any reference line crossing the caption is
 * masked and the text reads on top of it. A stroke rather than a backdrop
 * rect keeps the label text the svg's first child and leaves its bbox — and
 * so the anchoring above — unchanged.
 * @param {Array} lines - Caption rows, top first; falsy entries are skipped.
 * @param {string} color - Fill colour, matched to the reference line.
 * @param {object} pos - First row's `x`/`y` offsets, text anchor, and whether
 *   to `halo` the text over lines passing behind it.
 * @param {object} [size] - Explicit svg viewport; omit for the default.
 * @return {string} Markup for a `CustomAnnotation`'s `svgString`.
 */
function referenceLabelSvg(
  lines: (string | null | undefined)[],
  color: string,
  pos: { x: number; y: number; anchor?: 'start' | 'end'; halo?: boolean },
  size?: { width: number; height: number }
): string {
  const dims = size ? ` width="${size.width}" height="${size.height}"` : '';
  const haloAttrs = pos.halo
    ? ` stroke="${CHART_BACKGROUND}" stroke-width="4" stroke-linejoin="round" paint-order="stroke"`
    : '';
  const attrs = `x="${pos.x}" font-size="10" fill="${color}" text-anchor="${pos.anchor ?? 'start'}"${haloAttrs}`;
  const rows = lines
    .filter(Boolean)
    .map(
      (line, i) =>
        `<text ${attrs} y="${pos.y + i * REFERENCE_LABEL_LINE_HEIGHT}">${line}</text>`
    )
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg"${dims}>${rows}</svg>`;
}

/**
 * Value row for a reference-line caption, e.g. `412k bbl`.
 * @param {number} value - The metric value the reference line sits at.
 * @param {string} unit - Stream unit from the probit response; may be blank.
 * @return {string} The formatted value, with its unit when there is one.
 */
function valueText(value: number, unit: string): string {
  return `${formatCompact(value)}${unit ? ` ${unit}` : ''}`;
}

const AXIS_LABEL_STYLE = { fontSize: 11, padding: new Thickness(0, 6, 0, 0) };
const AXIS_TITLE_STYLE = { fontSize: 12 };
const HIT_TEST_RADIUS = 12;

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type Stream = 'oil' | 'gas' | 'water';
type YAxisMode = 'percentile' | 'probit';

const DEFAULT_POINT_COLOR = '#4589ff';

const PERCENTILE_TICKS = [
  0.01, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.99,
];

const PERCENTILE_LABELS: Record<number, string> = {
  0.01: 'P1',
  0.1: 'P10',
  0.2: 'P20',
  0.3: 'P30',
  0.4: 'P40',
  0.5: 'P50',
  0.6: 'P60',
  0.7: 'P70',
  0.8: 'P80',
  0.9: 'P90',
  0.99: 'P99',
};

class PercentileAxisTickProvider extends TickProvider {
  getMajorTicks(): number[] {
    return PERCENTILE_TICKS;
  }
  getMinorTicks(): number[] {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Point metadata
// ---------------------------------------------------------------------------

interface ProbitPointMeta extends IPointMetadata {
  uwi: string;
  rawValue: number;
  poe: number;
  /** Inverse-normal of `poe`, provided by the backend (`data.zScores[i]`). */
  zScore?: number;
  /** Optional display name (e.g. lease + well number). */
  wellName?: string;
  /** Optional qualifier label (e.g. "Well Control", "New Wells"). */
  wellQualifier?: string;
}

// ---------------------------------------------------------------------------
// Tooltip customization
// ---------------------------------------------------------------------------

/**
 * Context passed to a {@link ProbitTooltipRenderer}. Contains everything a
 * caller needs to produce a domain-specific tooltip without reaching back into
 * chart internals (e.g. the Type Curve probit plots produce a different
 * tooltip for each metric / Y-axis combination).
 */
export interface ProbitTooltipContext {
  uwi: string;
  wellName?: string;
  wellQualifier?: string;
  /** Raw metric value (X-axis value in non-swapped mode). */
  rawValue: number;
  /** Probability of exceedance in `[0, 1]`. */
  poe: number;
  /**
   * Inverse-normal of `poe` as provided by the backend (`data.zScores[i]`).
   * `null` when the payload doesn't include a z-score for the point —
   * callers should render a `--` fallback rather than compute one.
   */
  zScore: number | null;
  /** Current Y-axis mode — useful when tooltip labels should switch. */
  yMode: YAxisMode;
  /** Unit string from the response (e.g. "bbl", "mcf", "bbl/Mmcf"). */
  unit: string;
  /** X-axis label string as rendered on the chart (fallback label). */
  xAxisLabel: string;
  /** Compact number formatter used by the default tooltip (e.g. 2.31k, 1.2M). */
  formatCompact: (value: number) => string;
}

/**
 * Returns a full HTML string for the tooltip body. When provided, completely
 * replaces the default three-row tooltip. Use the {@link ProbitTooltipContext}
 * fields (including classnames via the default tooltip classes) to keep the
 * visual style consistent.
 *
 * CSS classnames exposed on {@link ProbitTooltipClassNames} are available to
 * the renderer so it can match the default look without duplicating styles.
 */
export type ProbitTooltipRenderer = (
  ctx: ProbitTooltipContext,
  classNames: ProbitTooltipClassNames
) => string;

export interface ProbitTooltipClassNames {
  row: string;
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Tooltip modifier
// ---------------------------------------------------------------------------

const HIGHLIGHT_HALO_ID = 'highlight-halo';
const HIGHLIGHT_BORDER_ID = 'highlight-border';
const HIGHLIGHT_INNER_ID = 'highlight-inner';
const CROSS_HIGHLIGHT_HALO_ID = 'cross-highlight-halo';
const CROSS_HIGHLIGHT_BORDER_ID = 'cross-highlight-border';
const CROSS_HIGHLIGHT_INNER_ID = 'cross-highlight-inner';
const HIGHLIGHT_IDS = new Set([
  HIGHLIGHT_HALO_ID,
  HIGHLIGHT_BORDER_ID,
  HIGHLIGHT_INNER_ID,
  CROSS_HIGHLIGHT_HALO_ID,
  CROSS_HIGHLIGHT_BORDER_ID,
  CROSS_HIGHLIGHT_INNER_ID,
]);

class ProbitTooltipModifier extends ChartModifierBase2D {
  readonly type = 'ProbitTooltipModifier';
  private tooltipEl: HTMLDivElement | null = null;
  private containerEl: HTMLDivElement | null = null;
  private valueLabel = '';
  private unit = '';
  private yMode: YAxisMode = 'percentile';
  private highlightDs: XyDataSeries | null = null;
  private highlightHalo: EllipsePointMarker | null = null;
  private highlightInner: EllipsePointMarker | null = null;
  private pointColor = DEFAULT_POINT_COLOR;
  private renderer: ProbitTooltipRenderer | null = null;
  private onPointHover: ((uwi: string | null) => void) | null = null;
  private lastHoveredUwi: string | null = null;

  setConfig(cfg: {
    tooltipEl: HTMLDivElement | null;
    containerEl: HTMLDivElement | null;
    valueLabel: string;
    unit: string;
    yMode: YAxisMode;
    highlightDs: XyDataSeries | null;
    highlightHalo: EllipsePointMarker | null;
    highlightInner: EllipsePointMarker | null;
    pointColor: string;
    renderer: ProbitTooltipRenderer | null;
    onPointHover: ((uwi: string | null) => void) | null;
  }) {
    this.tooltipEl = cfg.tooltipEl;
    this.containerEl = cfg.containerEl;
    this.valueLabel = cfg.valueLabel;
    this.unit = cfg.unit;
    this.yMode = cfg.yMode;
    this.highlightDs = cfg.highlightDs;
    this.highlightHalo = cfg.highlightHalo;
    this.highlightInner = cfg.highlightInner;
    this.pointColor = cfg.pointColor;
    this.renderer = cfg.renderer;
    this.onPointHover = cfg.onPointHover;
  }

  private updateHighlight(x?: number, y?: number) {
    if (!this.highlightDs) return;
    this.highlightDs.clear();
    if (x !== undefined && y !== undefined) {
      if (this.highlightHalo) {
        this.highlightHalo.fill = `${this.pointColor}50`;
      }
      if (this.highlightInner) {
        this.highlightInner.fill = this.pointColor;
        this.highlightInner.stroke = this.pointColor;
      }
      this.highlightDs.append(x, y);
    }
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    if (!this.tooltipEl || !this.parentSurface) return;

    let closest: {
      meta: ProbitPointMeta;
      dist: number;
      x: number;
      y: number;
    } | null = null;

    for (const series of this.parentSurface.renderableSeries.asArray()) {
      if (!series.isVisible || HIGHLIGHT_IDS.has(series.id)) continue;
      const ht = series.hitTestProvider.hitTest(
        args.mousePoint.x,
        args.mousePoint.y,
        HIT_TEST_RADIUS
      );
      if (ht.isHit && ht.dataSeriesIndex >= 0) {
        const ds = series.dataSeries as XyDataSeries;
        const meta = ds.getMetadataAt(ht.dataSeriesIndex) as ProbitPointMeta;
        if (!meta) continue;
        const dist = Math.hypot(
          args.mousePoint.x - ht.hitTestPointValues.x,
          args.mousePoint.y - ht.hitTestPointValues.y
        );
        if (!closest || dist < closest.dist) {
          closest = {
            meta,
            dist,
            x: ds.getNativeXValues().get(ht.dataSeriesIndex),
            y: ds.getNativeYValues().get(ht.dataSeriesIndex),
          };
        }
      }
    }

    if (!closest) {
      this.tooltipEl.style.display = 'none';
      this.updateHighlight();
      if (this.lastHoveredUwi !== null) {
        this.lastHoveredUwi = null;
        this.onPointHover?.(null);
      }
      return;
    }

    const { meta, x, y } = closest;
    this.updateHighlight(x, y);

    if (meta.uwi !== this.lastHoveredUwi) {
      this.lastHoveredUwi = meta.uwi;
      this.onPointHover?.(meta.uwi);
    }

    const classNames: ProbitTooltipClassNames = {
      row: styles.tooltipRow,
      label: styles.tooltipLabel,
      value: styles.tooltipValue,
    };

    if (this.renderer) {
      const ctx: ProbitTooltipContext = {
        uwi: meta.uwi,
        wellName: meta.wellName,
        wellQualifier: meta.wellQualifier,
        rawValue: meta.rawValue,
        poe: meta.poe,
        zScore: typeof meta.zScore === 'number' ? meta.zScore : null,
        yMode: this.yMode,
        unit: this.unit,
        xAxisLabel: this.valueLabel,
        formatCompact,
      };
      this.tooltipEl.innerHTML = this.renderer(ctx, classNames);
    } else {
      const poe = formatNumber(meta.poe * 100, 2, 2);
      const val = formatCompact(meta.rawValue);
      this.tooltipEl.innerHTML = `
        <div class="${classNames.row}"><span class="${classNames.label}">UWI:</span> <span class="${classNames.value}">${meta.uwi}</span></div>
        <div class="${classNames.row}"><span class="${classNames.label}">${this.valueLabel}:</span> <span class="${classNames.value}">${val}</span></div>
        <div class="${classNames.row}"><span class="${classNames.label}">Probability of Exceedance:</span> <span class="${classNames.value}">${poe}%</span></div>
      `;
    }
    this.tooltipEl.style.display = 'block';

    if (this.containerEl) {
      const rect = this.containerEl.getBoundingClientRect();
      const e = args.nativeEvent as MouseEvent;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const cW = rect.width;
      const cH = rect.height;
      const tW = this.tooltipEl.offsetWidth;
      const tH = this.tooltipEl.offsetHeight;

      let left = mx + 12;
      let top = my - tH - 8;

      if (top < 0) top = my + 12;
      if (left + tW > cW) left = mx - tW - 12;

      this.tooltipEl.style.left = `${Math.max(0, left)}px`;
      this.tooltipEl.style.top = `${Math.max(0, Math.min(top, cH - tH))}px`;
    }
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    if (this.tooltipEl) this.tooltipEl.style.display = 'none';
    this.updateHighlight();
    if (this.lastHoveredUwi !== null) {
      this.lastHoveredUwi = null;
      this.onPointHover?.(null);
    }
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export { type Stream, type YAxisMode };

/**
 * Optional annotation for the reference Type Curve. Renders as a coloured
 * vertical line (or horizontal when `axisSwapped`) at the TC's metric value,
 * with a small "P_" label at the interpolated pValue and a "Type Curve"
 * caption anchored to the opposite edge.
 */
export interface ProbitTypeCurveAnnotation {
  /** TC metric value → position on the log axis. */
  value: number;
  /** Interpolated pValue → label position along the probability axis.
   *  `null` when the TC value falls outside the well distribution;
   *  in that case the P-label is suppressed but the TC reference line
   *  is still drawn. */
  pValue: number | null;
  /** Formatted caption, e.g. "P27". `null` when `pValue` is `null`. */
  label: string | null;
  /** Optional stroke color. Defaults to a gold that matches the design. */
  color?: string;
}

export type ProbitChartData = ProbitData & {
  /** Optional reference Type Curve annotation. */
  typeCurve?: ProbitTypeCurveAnnotation | null;
  /**
   * Optional per-well metadata lookup keyed by UWI. When provided, values are
   * attached to each point's `meta` object and exposed to the tooltip
   * renderer — useful for pages (e.g. Type Curve probit plots) that need
   * additional well context beyond what the probit endpoint returns.
   */
  wellMetaByUwi?: Record<string, { name?: string; qualifier?: string }>;
};

export interface ProbitChartProps {
  /**
   * Full chart dataset. In addition to the scatter values, `data.typeCurve`
   * optionally drives the reference TC annotation and `data.wellMetaByUwi`
   * optionally surfaces per-well context to custom tooltip renderers.
   */
  data: ProbitChartData | null;
  uwis: string[];
  yMode: YAxisMode;
  axisSwapped: boolean;
  color?: string;
  xAxisLabel?: string;
  loading?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  /**
   * When provided, renders an icon-only image-export trigger in the chart
   * header (top-right) wired to this chart's SciChart surface. Omit to render
   * no trigger (default), leaving other consumers unaffected.
   */
  exportConfig?: { label: string; filenamePrefix: string };
  /**
   * Optional custom tooltip renderer. Completely replaces the default
   * three-row tooltip when provided. See {@link ProbitTooltipContext} /
   * {@link ProbitTooltipClassNames} for the call signature.
   */
  tooltipRenderer?: ProbitTooltipRenderer;
  /**
   * Optional UWI → hex color map for per-point coloring (color-by attribute).
   * Points whose UWI is not in the map use the default `color` prop.
   */
  uwiColorMap?: Record<string, string>;
  /**
   * UWI to highlight with a halo effect across charts (cross-chart linking).
   * When set, the matching point gets a persistent halo regardless of hover.
   */
  highlightedUwi?: string | null;
  /**
   * Callback fired when the user hovers a data point. Receives the UWI of
   * the hovered point, or `null` when the cursor leaves all points.
   */
  onPointHover?: (uwi: string | null) => void;
}

const TYPE_CURVE_LINE_COLOR = '#b28600';

export const ProbitChart: FC<ProbitChartProps> = ({
  data,
  uwis,
  yMode,
  axisSwapped,
  color = DEFAULT_POINT_COLOR,
  xAxisLabel,
  loading = false,
  header,
  footer,
  exportConfig,
  tooltipRenderer,
  uwiColorMap,
  highlightedUwi,
  onPointHover,
}) => {
  // Pull the optional annotation + well-meta off `data` so the rest of the
  // component can reference them as locals (keeps the dependency lists and
  // subsequent logic unchanged).
  const typeCurve: ProbitTypeCurveAnnotation | null = data?.typeCurve ?? null;
  const wellMetaByUwi = data?.wellMetaByUwi;
  const [isZoomed, setIsZoomed] = useState(false);
  const [surfaceReady, setSurfaceReady] = useState(false);

  const onPointHoverRef = useRef(onPointHover);
  onPointHoverRef.current = onPointHover;

  const surfaceRef = useRef<SciChartSurface | null>(null);
  // Scale axis-label/title fonts and series/marker weight to the export resolution.
  const exportElementHooks = useMemo(
    () => createSciChartElementHooks(() => surfaceRef.current),
    []
  );
  const wasmRef = useRef<TSciChart | null>(null);
  const initialXRangeRef = useRef<NumberRange | null>(null);
  const initialYRangeRef = useRef<NumberRange | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartDivRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const crossHighlightDsRef = useRef<XyDataSeries | null>(null);
  const crossHighlightInnerRef = useRef<EllipsePointMarker | null>(null);
  const pointsByUwiRef = useRef<Map<string, { x: number; y: number }>>(
    new Map()
  );
  const chartDivId = useRef(
    `probit-chart-${Math.random().toString(36).slice(2, 11)}`
  ).current;

  const isPercentileMode = yMode === 'percentile';

  // -----------------------------------------------------------------------
  // Compute chart data from API response
  // -----------------------------------------------------------------------

  const chartData = useMemo(() => {
    if (!data || !data.values.length) return null;

    const { values, pValues, percentileLines, unit } = data;
    const zScores = data.zScores;

    const sortedIndices = values
      .map((_, i) => i)
      .sort((a, b) => values[a] - values[b]);

    const points: { x: number; y: number; meta: ProbitPointMeta }[] = [];

    for (const origIdx of sortedIndices) {
      const metric = values[origIdx];
      const uwi = uwis[origIdx] ?? '';
      const yVal = pValues[origIdx];
      const extras = wellMetaByUwi?.[uwi];

      points.push({
        x: axisSwapped ? yVal : metric,
        y: axisSwapped ? metric : yVal,
        meta: {
          isSelected: false,
          uwi,
          rawValue: metric,
          poe: pValues[origIdx],
          zScore: zScores?.[origIdx],
          wellName: extras?.name,
          wellQualifier: extras?.qualifier,
        } as ProbitPointMeta,
      });
    }

    const metricValues = values.filter((v) => v > 0);
    const logMin =
      metricValues.length > 0
        ? Math.pow(10, Math.floor(Math.log10(Math.min(...metricValues))))
        : 1;
    const logMax =
      metricValues.length > 0
        ? Math.pow(10, Math.ceil(Math.log10(Math.max(...metricValues))))
        : 1e6;
    const numDecades = Math.round(Math.log10(logMax) - Math.log10(logMin));

    return {
      points,
      percentileLines,
      unit,
      metricLabel: xAxisLabel || `EUR [${unit}]`,
      logRange: { min: logMin, max: logMax, decades: numDecades },
    };
  }, [data, uwis, axisSwapped, xAxisLabel, wellMetaByUwi]);

  // -----------------------------------------------------------------------
  // Init SciChart surface
  // -----------------------------------------------------------------------

  useEffect(() => {
    ensureWasmConfigured();
    let deleted = false;

    const init = async () => {
      const theme = new SciChartJSLightTheme();
      theme.sciChartBackground = CHART_BACKGROUND;
      theme.majorGridLineBrush = '#d0d0d0';
      theme.minorGridLineBrush = '#e8e8e8';
      theme.axisBandsFill = 'transparent';

      const { sciChartSurface, wasmContext } = await SciChartSurface.create(
        chartDivId,
        { theme, padding: { left: 12, right: 14, top: 10, bottom: 4 } }
      );

      if (deleted) {
        sciChartSurface.delete();
        return;
      }

      surfaceRef.current = sciChartSurface;
      wasmRef.current = wasmContext;
      setSurfaceReady(true);
    };

    init();

    return () => {
      deleted = true;
      if (surfaceRef.current) {
        surfaceRef.current.delete();
        surfaceRef.current = null;
      }
    };
  }, [chartDivId]);

  // -----------------------------------------------------------------------
  // Rebuild chart when data/options change
  // -----------------------------------------------------------------------

  useEffect(() => {
    const surface = surfaceRef.current;
    const wasm = wasmRef.current;
    if (!surface || !wasm) return;

    surface.renderableSeries.clear();
    surface.xAxes.clear();
    surface.yAxes.clear();
    surface.annotations.clear();
    surface.chartModifiers.clear();

    if (!chartData) return;

    const { points, percentileLines, metricLabel, logRange, unit } = chartData;

    const axisBase = {
      drawMajorGridLines: true,
      drawMinorGridLines: true,
      labelStyle: AXIS_LABEL_STYLE,
      axisTitleStyle: AXIS_TITLE_STYLE,
    };

    // =================================================================
    // Axes
    // =================================================================

    const pctLabel = 'Probability of Exceedance [%]';
    const probitLabel = 'Probit';
    const probAxisTitle = isPercentileMode ? pctLabel : probitLabel;

    const logAxisRange = new NumberRange(logRange.min, logRange.max);
    const probAxisRange = new NumberRange(-0.02, 1.02);

    const pctLabelFormatter = (value: number) => {
      const closest = PERCENTILE_TICKS.reduce((prev, curr) => {
        return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
      });
      if (Math.abs(closest - value) < 0.005) {
        return PERCENTILE_LABELS[closest] ?? ' ';
      }
      return ' ';
    };

    if (axisSwapped) {
      // X = probability, Y = metric (log)
      const xLabelProvider = new NumericLabelProvider();
      xLabelProvider.formatLabel = pctLabelFormatter;

      const xProbAxis = new NumericAxis(wasm, {
        ...axisBase,
        drawMinorGridLines: false,
        axisTitle: probAxisTitle,
        visibleRange: probAxisRange,
        autoRange: EAutoRange.Never,
        flippedCoordinates: true,
        labelProvider: xLabelProvider,
      });
      xProbAxis.tickProvider = new PercentileAxisTickProvider();
      surface.xAxes.add(xProbAxis);

      const yLogLabel = new NumericLabelProvider();
      yLogLabel.formatLabel = formatDecadeLabel;
      surface.yAxes.add(
        new LogarithmicAxis(wasm, {
          ...axisBase,
          axisAlignment: EAxisAlignment.Left,
          axisTitle: metricLabel,
          logBase: 10,
          visibleRange: new NumberRange(logRange.min, logRange.max),
          maxAutoTicks: logRange.decades + 1,
          minorsPerMajor: 9,
          labelProvider: yLogLabel,
        })
      );
    } else {
      // X = metric (log), Y = probability
      const xLogLabel = new NumericLabelProvider();
      xLogLabel.formatLabel = formatDecadeLabel;

      surface.xAxes.add(
        new LogarithmicAxis(wasm, {
          ...axisBase,
          axisTitle: metricLabel,
          logBase: 10,
          visibleRange: new NumberRange(logRange.min, logRange.max),
          maxAutoTicks: logRange.decades + 1,
          minorsPerMajor: 9,
          labelProvider: xLogLabel,
        })
      );

      const yLabelProvider = new NumericLabelProvider();
      yLabelProvider.formatLabel = pctLabelFormatter;

      const yProbAxis = new NumericAxis(wasm, {
        ...axisBase,
        drawMinorGridLines: false,
        axisAlignment: EAxisAlignment.Left,
        axisTitle: probAxisTitle,
        visibleRange: probAxisRange,
        autoRange: EAutoRange.Never,
        flippedCoordinates: true,
        labelProvider: yLabelProvider,
      });
      yProbAxis.tickProvider = new PercentileAxisTickProvider();
      surface.yAxes.add(yProbAxis);
    }

    // =================================================================
    // Scatter series
    // =================================================================

    if (points.length > 0) {
      // Build UWI → coordinate lookup for cross-chart highlight
      const uwiMap = new Map<string, { x: number; y: number }>();
      for (const p of points) {
        if (p.meta.uwi) uwiMap.set(p.meta.uwi, { x: p.x, y: p.y });
      }
      pointsByUwiRef.current = uwiMap;

      // Group points by resolved color for per-point coloring
      const colorGroups = new Map<
        string,
        { x: number[]; y: number[]; meta: ProbitPointMeta[] }
      >();

      for (const p of points) {
        const ptColor = uwiColorMap?.[p.meta.uwi] ?? color;
        let group = colorGroups.get(ptColor);
        if (!group) {
          group = { x: [], y: [], meta: [] };
          colorGroups.set(ptColor, group);
        }
        group.x.push(p.x);
        group.y.push(p.y);
        group.meta.push(p.meta);
      }

      let seriesIdx = 0;
      for (const [ptColor, group] of colorGroups) {
        const ds = new XyDataSeries(wasm, {
          xValues: group.x,
          yValues: group.y,
          metadata: group.meta,
          dataSeriesName: seriesIdx === 0 ? 'Wells' : `Wells-${seriesIdx}`,
        });

        surface.renderableSeries.add(
          new XyScatterRenderableSeries(wasm, {
            id: `wells-${seriesIdx}`,
            dataSeries: ds,
            pointMarker: new EllipsePointMarker(wasm, {
              width: 5,
              height: 5,
              fill: ptColor,
              stroke: ptColor,
              strokeThickness: 0,
            }),
            opacity: 0.85,
          })
        );
        seriesIdx++;
      }
    }

    // 3-layer highlight: halo → white border → inner dot
    const highlightDs = new XyDataSeries(wasm);

    const highlightHaloMarker = new EllipsePointMarker(wasm, {
      width: 25,
      height: 25,
      fill: `${color}50`,
      stroke: 'transparent',
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasm, {
        id: HIGHLIGHT_HALO_ID,
        dataSeries: highlightDs,
        pointMarker: highlightHaloMarker,
        opacity: 1,
      })
    );

    const highlightBorderMarker = new EllipsePointMarker(wasm, {
      width: 14,
      height: 14,
      fill: '#ffffff',
      stroke: '#ffffff',
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasm, {
        id: HIGHLIGHT_BORDER_ID,
        dataSeries: highlightDs,
        pointMarker: highlightBorderMarker,
        opacity: 1,
      })
    );

    const highlightInnerMarker = new EllipsePointMarker(wasm, {
      width: 10,
      height: 10,
      fill: color,
      stroke: color,
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasm, {
        id: HIGHLIGHT_INNER_ID,
        dataSeries: highlightDs,
        pointMarker: highlightInnerMarker,
        opacity: 1,
      })
    );

    // Cross-chart highlight (3-layer, same as hover but driven by prop)
    const crossDs = new XyDataSeries(wasm);
    crossHighlightDsRef.current = crossDs;

    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasm, {
        id: CROSS_HIGHLIGHT_HALO_ID,
        dataSeries: crossDs,
        pointMarker: new EllipsePointMarker(wasm, {
          width: 25,
          height: 25,
          fill: `${color}50`,
          stroke: 'transparent',
          strokeThickness: 0,
        }),
        opacity: 1,
      })
    );
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasm, {
        id: CROSS_HIGHLIGHT_BORDER_ID,
        dataSeries: crossDs,
        pointMarker: new EllipsePointMarker(wasm, {
          width: 14,
          height: 14,
          fill: '#ffffff',
          stroke: '#ffffff',
          strokeThickness: 0,
        }),
        opacity: 1,
      })
    );
    crossHighlightInnerRef.current = new EllipsePointMarker(wasm, {
      width: 10,
      height: 10,
      fill: color,
      stroke: color,
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasm, {
        id: CROSS_HIGHLIGHT_INNER_ID,
        dataSeries: crossDs,
        pointMarker: crossHighlightInnerRef.current,
        opacity: 1,
      })
    );

    // =================================================================
    // Percentile reference lines
    // =================================================================

    if (percentileLines) {
      for (const [pLabel, pVal] of Object.entries(percentileLines)) {
        const isP50 = pLabel === '50';
        const lineColor = isP50 ? '#161616' : '#525252';
        const thickness = isP50 ? 2 : 1.5;
        const dash = isP50 ? undefined : [6, 4];
        // Only the median line carries its value; P10/P90 stay label-only so
        // the three captions don't crowd each other near the axis.
        const rows = [`P${pLabel}`, isP50 ? valueText(pVal, unit) : null];
        // The P50 caption is haloed so it reads on top of any line crossing it,
        // like the Type Curve captions.
        const halo = isP50;

        if (axisSwapped) {
          surface.annotations.add(
            new HorizontalLineAnnotation({
              y1: pVal,
              stroke: lineColor,
              strokeThickness: thickness,
              strokeDashArray: dash,
              showLabel: false,
              annotationLayer: EAnnotationLayer.BelowChart,
            })
          );

          surface.annotations.add(
            new CustomAnnotation({
              x1: 0,
              y1: pVal,
              xCoordinateMode: ECoordinateMode.Relative,
              horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
              verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
              svgString: referenceLabelSvg(rows, lineColor, {
                x: 3,
                y: -2,
                halo,
              }),
            })
          );
        } else {
          surface.annotations.add(
            new VerticalLineAnnotation({
              x1: pVal,
              stroke: lineColor,
              strokeThickness: thickness,
              strokeDashArray: dash,
              showLabel: false,
              annotationLayer: EAnnotationLayer.BelowChart,
            })
          );

          surface.annotations.add(
            new CustomAnnotation({
              x1: pVal,
              y1: 0.03,
              yCoordinateMode: ECoordinateMode.Relative,
              horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
              verticalAnchorPoint: EVerticalAnchorPoint.Top,
              svgString: referenceLabelSvg(rows, lineColor, {
                x: 3,
                y: 12,
                halo,
              }),
            })
          );
        }
      }
    }

    // =================================================================
    // Type Curve reference line (single annotation — gold / goldenrod)
    // =================================================================

    if (typeCurve && typeCurve.value > 0) {
      const tcColor = typeCurve.color ?? TYPE_CURVE_LINE_COLOR;
      const tcPLabel = typeCurve.label;
      const tcPValue = typeCurve.pValue;
      // Only draw the P-label when both fields are present; backend sends
      // `null` for `pValue`/`label` when the TC value falls outside the
      // well distribution (and there are no wells to interpolate against).
      const canDrawPLabel = tcPLabel !== null && tcPValue !== null;

      const tcValueText = valueText(typeCurve.value, unit);

      // Gold text, regular weight to match design. A white halo under the
      // glyphs masks the percentile (and any other) reference lines crossing
      // the caption, so it reads on top of them; added after the percentile
      // captions, it also sits above those where they overlap. `AboveChart`
      // layer keeps it on top of scatter + crosshair.
      // The svg viewport grows when a value row rides along, so neither the
      // second row nor a long value + unit ("1.23M bbl/Mmcf") is clipped by
      // the single-row box. Size is the clip viewport only — SciChart anchors
      // off the first row's own bbox, so this doesn't move the caption.
      const tcLabelSvg = (
        text: string,
        anchor: 'start' | 'end',
        value?: string | null
      ) =>
        referenceLabelSvg(
          [text, value],
          tcColor,
          { x: anchor === 'end' ? -4 : 4, y: 12, anchor, halo: true },
          value ? { width: 120, height: 30 } : { width: 80, height: 16 }
        );

      if (axisSwapped) {
        // Horizontal line at Y = TC value (log axis); labels anchored on
        // either side along the probability X-axis.
        surface.annotations.add(
          new HorizontalLineAnnotation({
            y1: typeCurve.value,
            stroke: tcColor,
            strokeThickness: 2,
            showLabel: false,
            annotationLayer: EAnnotationLayer.BelowChart,
          })
        );
        // P-label near the TC's interpolated pValue on the probability axis.
        if (canDrawPLabel) {
          surface.annotations.add(
            new CustomAnnotation({
              x1: tcPValue,
              y1: typeCurve.value,
              horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
              verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
              annotationLayer: EAnnotationLayer.AboveChart,
              svgString: tcLabelSvg(tcPLabel, 'start'),
            })
          );
        }
        // "Type Curve" caption anchored inside the right edge.
        surface.annotations.add(
          new CustomAnnotation({
            x1: 0.98,
            y1: typeCurve.value,
            xCoordinateMode: ECoordinateMode.Relative,
            horizontalAnchorPoint: EHorizontalAnchorPoint.Right,
            verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
            annotationLayer: EAnnotationLayer.AboveChart,
            svgString: tcLabelSvg('Type Curve', 'end', tcValueText),
          })
        );
      } else {
        // Vertical line at X = TC value (log axis); labels anchored at
        // top (P-value) and bottom ("Type Curve") of the plot area.
        surface.annotations.add(
          new VerticalLineAnnotation({
            x1: typeCurve.value,
            stroke: tcColor,
            strokeThickness: 2,
            showLabel: false,
            annotationLayer: EAnnotationLayer.BelowChart,
          })
        );
        if (canDrawPLabel) {
          surface.annotations.add(
            new CustomAnnotation({
              x1: typeCurve.value,
              y1: 0.03,
              yCoordinateMode: ECoordinateMode.Relative,
              horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
              verticalAnchorPoint: EVerticalAnchorPoint.Top,
              annotationLayer: EAnnotationLayer.AboveChart,
              svgString: tcLabelSvg(tcPLabel, 'start'),
            })
          );
        }
        // "Type Curve" caption — anchored ~88% down so both it and the value
        // line beneath sit well above the x-axis gutter + SciChart watermark
        // (which sits bottom-left). Was 92% when this was a single line.
        surface.annotations.add(
          new CustomAnnotation({
            x1: typeCurve.value,
            y1: 0.88,
            yCoordinateMode: ECoordinateMode.Relative,
            horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
            verticalAnchorPoint: EVerticalAnchorPoint.Top,
            annotationLayer: EAnnotationLayer.AboveChart,
            svgString: tcLabelSvg('Type Curve', 'start', tcValueText),
          })
        );
      }
    }

    // =================================================================
    // Chart modifiers
    // =================================================================

    surface.chartModifiers.add(new MouseWheelZoomModifier());
    surface.chartModifiers.add(new ZoomPanModifier());
    surface.chartModifiers.add(new XAxisDragModifier());
    surface.chartModifiers.add(new YAxisDragModifier());
    surface.chartModifiers.add(
      new CursorModifier({
        showTooltip: false,
        showAxisLabels: false,
        crosshairStroke: '#a8a8a8',
        crosshairStrokeDashArray: [4, 4],
      })
    );

    const reapplyRanges = (s: SciChartSurface) => {
      const logAxis = axisSwapped ? s.yAxes.get(0) : s.xAxes.get(0);
      const probAxis = axisSwapped ? s.xAxes.get(0) : s.yAxes.get(0);
      if (logAxis) logAxis.visibleRange = logAxisRange;
      if (probAxis) probAxis.visibleRange = probAxisRange;
    };

    class ResetZoomModifier extends ChartModifierBase2D {
      readonly type = 'ResetZoomModifier';
      override modifierDoubleClick(args: ModifierMouseArgs) {
        if (!this.parentSurface) return;
        this.parentSurface.zoomExtents();
        reapplyRanges(this.parentSurface);
        setIsZoomed(false);
        args.handled = true;
      }
    }
    surface.chartModifiers.add(new ResetZoomModifier());

    const tooltipMod = new ProbitTooltipModifier();
    tooltipMod.setConfig({
      tooltipEl: tooltipRef.current ?? null,
      containerEl: containerRef.current ?? null,
      valueLabel: metricLabel || 'Value',
      unit: chartData.unit,
      yMode,
      highlightDs,
      highlightHalo: highlightHaloMarker,
      highlightInner: highlightInnerMarker,
      pointColor: color,
      renderer: tooltipRenderer ?? null,
      onPointHover: onPointHoverRef.current ?? null,
    });
    surface.chartModifiers.add(tooltipMod);

    surface.zoomExtents();
    reapplyRanges(surface);
    initialXRangeRef.current = surface.xAxes.get(0)?.visibleRange ?? null;
    initialYRangeRef.current = surface.yAxes.get(0)?.visibleRange ?? null;
    setIsZoomed(false);

    const xAxis = surface.xAxes.get(0);
    if (xAxis) {
      xAxis.visibleRangeChanged.subscribe(() => setIsZoomed(true));
    }
  }, [
    surfaceReady,
    chartData,
    axisSwapped,
    isPercentileMode,
    yMode,
    color,
    typeCurve,
    tooltipRenderer,
    uwiColorMap,
  ]);

  // -----------------------------------------------------------------------
  // Cross-chart highlight — driven by `highlightedUwi` prop
  // -----------------------------------------------------------------------

  useEffect(() => {
    const ds = crossHighlightDsRef.current;
    if (!ds) return;
    ds.clear();

    if (!highlightedUwi) return;
    const pt = pointsByUwiRef.current.get(highlightedUwi);
    if (!pt) return;

    // Resolve the point's color from the color map
    const ptColor = uwiColorMap?.[highlightedUwi] ?? color;
    const inner = crossHighlightInnerRef.current;
    if (inner) {
      inner.fill = ptColor;
      inner.stroke = ptColor;
    }

    ds.append(pt.x, pt.y);
  }, [highlightedUwi, color, uwiColorMap]);

  // -----------------------------------------------------------------------
  // Reset zoom
  // -----------------------------------------------------------------------

  const handleResetZoom = useCallback(() => {
    const s = surfaceRef.current;
    if (!s) return;
    s.zoomExtents();
    if (initialXRangeRef.current)
      s.xAxes.get(0).visibleRange = initialXRangeRef.current;
    if (initialYRangeRef.current)
      s.yAxes.get(0).visibleRange = initialYRangeRef.current;
    setIsZoomed(false);
  }, []);

  // -----------------------------------------------------------------------
  // Responsive resizing — keep SciChart canvas in sync with container
  // -----------------------------------------------------------------------

  const handleResize = useMemo(
    () =>
      debounce((entries: ResizeObserverEntry[]) => {
        const { width, height } = entries[entries.length - 1].contentRect;
        const divEl = document.getElementById(chartDivId);
        if (divEl) {
          divEl.style.width = `${width}px`;
          divEl.style.height = `${height}px`;
        }
      }, 200),
    [chartDivId]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);

    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
    };
  }, [handleResize]);

  return (
    <div className={styles.chartCard}>
      {header}

      {exportConfig && (
        <SciChartExportImageButton
          iconOnly
          surfaceRef={surfaceRef}
          containerRef={containerRef}
          chartLabel={exportConfig.label}
          filenamePrefix={exportConfig.filenamePrefix}
          elementHooks={exportElementHooks}
        />
      )}

      <div ref={containerRef} className={styles.chartInner}>
        <div ref={chartDivRef} id={chartDivId} className={styles.hcContainer} />

        {isZoomed && (
          <button className={styles.resetButton} onClick={handleResetZoom}>
            Reset zoom
          </button>
        )}

        <div ref={tooltipRef} className={styles.tooltip} />

        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
              fontSize: '14px',
              color: '#666',
            }}
          >
            Loading...
          </div>
        )}

        {!chartData && !loading && (
          <div className={styles.emptyState}>No data available</div>
        )}
      </div>

      {footer}
    </div>
  );
};
